import { z, ZodError, ZodSchema } from 'zod';

import { Config } from '@nw/shared/config';

import { statusCodes } from '../../constants/APIErrorConstants';

import { captureSentryException } from '../SentryUtils';

export enum ZodValidationType {
  REQUEST = 'REQUEST',
  RESPONSE = 'RESPONSE'
}

export type ZodValidationAdditionalData = {
  endpoint?: string;
  operationName?: string;
  type: ZodValidationType;
};

export const ValidateWithZodAndReportError = <T>(
  validationData: T,
  zodValidatorSchema: ZodSchema<T>,
  additionalData?: ZodValidationAdditionalData,
  shouldReportError = true
): T | void => {
  try {
    return zodValidatorSchema.parse(validationData);
  } catch (err) {
    if (shouldReportError) {
      const zodError = err as ZodError;
      if (Config['ENVIRONMENT'] !== 'ccbp_prod') {
        console.error(zodError);
      }
      const sentryError = new Error('Zod Validation Error');
      captureSentryException(sentryError, {
        errorInfo: zodError,
        validationData,
        ...additionalData
      });
    }
  }
};

const zodErrorData = {
  res_status: '',
  response: 'Something went wrong',
  http_status_code: statusCodes.badRequestErrorCode
};
const customZodErrorObject = new Error(JSON.stringify(zodErrorData));

export function validateApiResponseWithZod<T>(
  validationData: T,
  zodValidatorSchema: ZodSchema<T>
): T | void {
  const parsedResponse = ValidateWithZodAndReportError(
    validationData,
    zodValidatorSchema,
    { type: ZodValidationType.RESPONSE }
  );

  if (parsedResponse) {
    return parsedResponse;
  }
  throw customZodErrorObject;
}

export function validateJSONString<T>(
  schema: z.ZodType<T, any, any>,
  isNullable: boolean,
  message?: string
) {
  return (data: string | null | undefined) => {
    if (isNullable && (data === null || data === undefined)) return true;
    try {
      const parsed = JSON.parse(data ?? '""');
      schema.parse(parsed);
      return true;
    } catch (e) {
      const error = message ? message : e;
      throw new Error(JSON.stringify(error));
    }
  };
}
